import { __assign } from "tslib";
import { Batch, combine, deepMerge, getTimestamp, HttpRequest, includes, monitor, msToNs, RequestType, ResourceKind, withSnakeCaseKeys, } from '@datadog/browser-core';
import { LifeCycleEventType } from './lifeCycle';
import { matchRequestTiming } from './matchRequestTiming';
import { startParentContexts } from './parentContexts';
import { computePerformanceResourceDetails, computePerformanceResourceDuration, computeResourceKind, computeSize, isValidResource, } from './resourceUtils';
import { UserActionType } from './userActionCollection';
import { startViewCollection } from './viewCollection';
export var RumEventCategory;
(function (RumEventCategory) {
    RumEventCategory["USER_ACTION"] = "user_action";
    RumEventCategory["ERROR"] = "error";
    RumEventCategory["LONG_TASK"] = "long_task";
    RumEventCategory["VIEW"] = "view";
    RumEventCategory["RESOURCE"] = "resource";
})(RumEventCategory || (RumEventCategory = {}));
var SessionType;
(function (SessionType) {
    SessionType["SYNTHETICS"] = "synthetics";
    SessionType["USER"] = "user";
})(SessionType || (SessionType = {}));
export function startRum(applicationId, location, lifeCycle, configuration, session, internalMonitoring) {
    var globalContext = {};
    var parentContexts = startParentContexts(lifeCycle, session);
    internalMonitoring.setExternalContextProvider(function () {
        return deepMerge({
            application_id: applicationId,
        }, parentContexts.findView(), globalContext);
    });
    var batch = makeRumBatch(configuration, lifeCycle);
    var handler = makeRumEventHandler(parentContexts, session, function () { return ({
        applicationId: applicationId,
        date: new Date().getTime(),
        session: {
            // must be computed on each event because synthetics instrumentation can be done after sdk execution
            // cf https://github.com/puppeteer/puppeteer/issues/3667
            type: getSessionType(),
        },
        view: {
            referrer: document.referrer,
        },
    }); }, function () { return globalContext; });
    trackRumEvents(configuration, lifeCycle, session, handler, batch);
    startViewCollection(location, lifeCycle);
    return {
        globalApi: {
            addRumGlobalContext: monitor(function (key, value) {
                globalContext[key] = value;
            }),
            addUserAction: monitor(function (name, context) {
                lifeCycle.notify(LifeCycleEventType.CUSTOM_ACTION_COLLECTED, { context: context, name: name, type: UserActionType.CUSTOM });
            }),
            getInternalContext: monitor(function (startTime) {
                return withSnakeCaseKeys(deepMerge({ applicationId: applicationId }, parentContexts.findView(startTime), parentContexts.findAction(startTime)));
            }),
            setRumGlobalContext: monitor(function (context) {
                globalContext = context;
            }),
        },
        stop: function () {
            // prevent batch from previous tests to keep running and send unwanted requests
            // could be replaced by stopping all the component when they will all have a stop method
            batch.stop();
        },
    };
}
function makeRumBatch(configuration, lifeCycle) {
    var primaryBatch = createRumBatch(configuration.rumEndpoint);
    var replicaBatch;
    var replica = configuration.replica;
    if (replica !== undefined) {
        replicaBatch = createRumBatch(replica.rumEndpoint);
    }
    function createRumBatch(endpointUrl) {
        return new Batch(new HttpRequest(endpointUrl, configuration.batchBytesLimit, true), configuration.maxBatchSize, configuration.batchBytesLimit, configuration.maxMessageSize, configuration.flushTimeout, function () { return lifeCycle.notify(LifeCycleEventType.BEFORE_UNLOAD); });
    }
    function withReplicaApplicationId(message) {
        return deepMerge(message, { application_id: replica.applicationId });
    }
    var stopped = false;
    return {
        add: function (message) {
            if (stopped) {
                return;
            }
            primaryBatch.add(message);
            if (replicaBatch) {
                replicaBatch.add(withReplicaApplicationId(message));
            }
        },
        stop: function () {
            stopped = true;
        },
        upsert: function (message, key) {
            if (stopped) {
                return;
            }
            primaryBatch.upsert(message, key);
            if (replicaBatch) {
                replicaBatch.upsert(withReplicaApplicationId(message), key);
            }
        },
    };
}
function makeRumEventHandler(parentContexts, session, rumContextProvider, globalContextProvider) {
    return function rumEventHandler(assemble, callback) {
        return function (startTime, event, customerContext) {
            var view = parentContexts.findView(startTime);
            if (session.isTracked() && view && view.sessionId) {
                var action = parentContexts.findAction(startTime);
                var rumEvent = assemble(event, { action: action, view: view, rum: rumContextProvider() });
                var message = deepMerge(globalContextProvider(), customerContext, withSnakeCaseKeys(rumEvent));
                callback(message, rumEvent);
            }
        };
    };
}
function getSessionType() {
    return window._DATADOG_SYNTHETICS_BROWSER === undefined ? SessionType.USER : SessionType.SYNTHETICS;
}
function trackRumEvents(configuration, lifeCycle, session, handler, batch) {
    var assembleWithoutAction = function (event, _a) {
        var view = _a.view, rum = _a.rum;
        return combine(rum, view, event);
    };
    var assembleWithAction = function (event, _a) {
        var view = _a.view, action = _a.action, rum = _a.rum;
        return combine(rum, view, action, event);
    };
    trackView(lifeCycle, handler(assembleWithoutAction, function (message, event) { return batch.upsert(message, event.view.id); }));
    trackErrors(lifeCycle, handler(assembleWithAction, batch.add));
    trackRequests(configuration, lifeCycle, session, handler(assembleWithAction, batch.add));
    trackPerformanceTiming(configuration, lifeCycle, handler(assembleWithAction, batch.add));
    trackCustomUserAction(lifeCycle, handler(assembleWithoutAction, batch.add));
    trackAutoUserAction(lifeCycle, handler(assembleWithoutAction, batch.add));
}
export function trackView(lifeCycle, handler) {
    lifeCycle.subscribe(LifeCycleEventType.VIEW_UPDATED, function (view) {
        handler(view.startTime, {
            date: getTimestamp(view.startTime),
            duration: msToNs(view.duration),
            evt: {
                category: RumEventCategory.VIEW,
            },
            rum: {
                documentVersion: view.documentVersion,
            },
            view: {
                loadingTime: msToNs(view.loadingTime),
                loadingType: view.loadingType,
                measures: __assign(__assign({}, view.measures), { domComplete: msToNs(view.measures.domComplete), domContentLoaded: msToNs(view.measures.domContentLoaded), domInteractive: msToNs(view.measures.domInteractive), firstContentfulPaint: msToNs(view.measures.firstContentfulPaint), loadEventEnd: msToNs(view.measures.loadEventEnd) }),
            },
        });
    });
}
function trackErrors(lifeCycle, handler) {
    lifeCycle.subscribe(LifeCycleEventType.ERROR_COLLECTED, function (_a) {
        var message = _a.message, startTime = _a.startTime, context = _a.context;
        handler(startTime, __assign({ message: message, date: getTimestamp(startTime), evt: {
                category: RumEventCategory.ERROR,
            } }, context));
    });
}
function trackCustomUserAction(lifeCycle, handler) {
    lifeCycle.subscribe(LifeCycleEventType.CUSTOM_ACTION_COLLECTED, function (userAction) {
        handler(performance.now(), {
            evt: {
                category: RumEventCategory.USER_ACTION,
                name: userAction.name,
            },
            userAction: {
                type: userAction.type,
            },
        }, userAction.context);
    });
}
function trackAutoUserAction(lifeCycle, handler) {
    lifeCycle.subscribe(LifeCycleEventType.AUTO_ACTION_COMPLETED, function (userAction) {
        handler(userAction.startTime, {
            date: getTimestamp(userAction.startTime),
            duration: msToNs(userAction.duration),
            evt: {
                category: RumEventCategory.USER_ACTION,
                name: userAction.name,
            },
            userAction: {
                id: userAction.id,
                measures: userAction.measures,
                type: userAction.type,
            },
        });
    });
}
function trackRequests(configuration, lifeCycle, session, handler) {
    lifeCycle.subscribe(LifeCycleEventType.REQUEST_COMPLETED, function (request) {
        if (!session.isTrackedWithResource()) {
            return;
        }
        if (!isValidResource(request.url, configuration)) {
            return;
        }
        var timing = matchRequestTiming(request);
        var kind = request.type === RequestType.XHR ? ResourceKind.XHR : ResourceKind.FETCH;
        var startTime = timing ? timing.startTime : request.startTime;
        handler(startTime, {
            date: getTimestamp(startTime),
            duration: timing ? computePerformanceResourceDuration(timing) : msToNs(request.duration),
            evt: {
                category: RumEventCategory.RESOURCE,
            },
            http: {
                method: request.method,
                performance: timing ? computePerformanceResourceDetails(timing) : undefined,
                statusCode: request.status,
                url: request.url,
            },
            network: {
                bytesWritten: timing ? computeSize(timing) : undefined,
            },
            resource: {
                kind: kind,
            },
            traceId: request.traceId,
        });
        lifeCycle.notify(LifeCycleEventType.RESOURCE_ADDED_TO_BATCH);
    });
}
function trackPerformanceTiming(configuration, lifeCycle, handler) {
    lifeCycle.subscribe(LifeCycleEventType.PERFORMANCE_ENTRY_COLLECTED, function (entry) {
        switch (entry.entryType) {
            case 'resource':
                handleResourceEntry(configuration, entry, handler, lifeCycle);
                break;
            case 'longtask':
                handleLongTaskEntry(entry, handler);
                break;
            default:
                break;
        }
    });
}
export function handleResourceEntry(configuration, entry, handler, lifeCycle) {
    if (!isValidResource(entry.name, configuration)) {
        return;
    }
    var resourceKind = computeResourceKind(entry);
    if (includes([ResourceKind.XHR, ResourceKind.FETCH], resourceKind)) {
        return;
    }
    handler(entry.startTime, {
        date: getTimestamp(entry.startTime),
        duration: computePerformanceResourceDuration(entry),
        evt: {
            category: RumEventCategory.RESOURCE,
        },
        http: {
            performance: computePerformanceResourceDetails(entry),
            url: entry.name,
        },
        network: {
            bytesWritten: computeSize(entry),
        },
        resource: {
            kind: resourceKind,
        },
    });
    lifeCycle.notify(LifeCycleEventType.RESOURCE_ADDED_TO_BATCH);
}
function handleLongTaskEntry(entry, handler) {
    handler(entry.startTime, {
        date: getTimestamp(entry.startTime),
        duration: msToNs(entry.duration),
        evt: {
            category: RumEventCategory.LONG_TASK,
        },
    });
}
//# sourceMappingURL=rum.js.map